import { defineStore, storeToRefs } from 'pinia'
import type { Undefinable } from 'ts-helpers'
import { computed, ref, shallowRef } from 'vue'
import { useConfiguration } from '@/5_entities/Configuration'
import { useHandlePageError } from '@/6_shared/api'
import { usePageLoading } from '@/6_shared/composables'
import type { Pagination } from '@/6_shared/model'
import { categoryService } from '../api'
import { type Category, type CategoryDTO, type CategoryFilters, CategoryAdapter } from '../model'

export const useCategory = defineStore('category', () => {
  const { handlePageError } = useHandlePageError()

  //category
  const category = ref<Undefinable<Category>>(undefined)
  const setCategory = (categoryData: CategoryDTO) =>
    (category.value = new CategoryAdapter(categoryData))
  const categoryLoadError = ref(false)

  const { initialized: categoryInitialized, load: loadCategory } = usePageLoading()
  const getCategory = (id: number, filters?: CategoryFilters) => {
    loadCategory(
      async () => {
        categoryLoadError.value = false
        const category = await categoryService.get(id, filters)
        setCategory(category)
      },
      (error) => {
        categoryLoadError.value = true
        handlePageError(error)
      }
    )
  }

  //events
  const pagination = computed((): Undefinable<Pagination> => category.value?.eventsPagination)
  const allEventsLoaded = computed(
    () => !!pagination.value && pagination.value.currentPage >= pagination.value.pagesCount
  )

  const loadNextEventsPage = async (filters: CategoryFilters) => {
    try {
      if (!category.value || !pagination.value) return

      const newEvents = await categoryService.loadEvents(category.value.id, {
        ...filters,
        pagination: {
          ...filters.pagination,
          currentPage: pagination.value.currentPage + 1
        }
      })

      category.value.addEventsNextPage && category.value.addEventsNextPage(newEvents.repertoire!)
    } catch (e) {
      console.log(e)

      throw e
    }
  }

  //all categories
  const { categoriesNav } = storeToRefs(useConfiguration())
  const categories = shallowRef<Undefinable<Category[]>>(undefined)
  const setCategories = (categoriesData: CategoryDTO[]) => {
    categoriesData.sort((itemA, itemB) => itemB.rank - itemA.rank)
    categories.value =
      categoriesData
        .map((category) => new CategoryAdapter(category))
        //убираем категорию "другое"
        .filter((item) => item.id > 0 && item.repertoire?.length) ?? []
  }

  const { initialized: categoriesInitialized, load: loadCategories } = usePageLoading()
  const getAllCategories = async (
    filters: CategoryFilters = {
      pagination: { pageSize: 8, currentPage: 1 },
      filter: {}
    },
    ignoreErrors = false
  ) => {
    await loadCategories(
      async () => {
        if (!categoriesNav.value) return

        categories.value = undefined
        const categoriesDTOs = await categoryService.getAll(filters)
        setCategories(categoriesDTOs)
      },
      (error: any) => {
        !ignoreErrors && handlePageError(error)
        throw error
      }
    )
  }

  return {
    //category
    category,
    getCategory,
    categoryInitialized,
    categoryLoadError,
    //events
    pagination,
    allEventsLoaded,
    loadNextEventsPage,
    //all categories
    categories,
    categoriesInitialized,
    getAllCategories
  }
})
